import React, { useEffect } from 'react';

import { Container, Paper } from '@mui/material';
import { observer } from 'mobx-react';
import { useWindowSize } from 'react-use';

import classes from './NgzChallengeLeaderboard.module.scss';
import { angularizeWithProviders } from '../../../shared/providers';
import { ChallengesHeader } from '../../components/ChallengesHeader/ChallengesHeader';
import {
  BlackBgRow,
  EmptyList,
  LbPagination,
} from '../../../shared/components/ui-components';
import { ChallengeTitleBlock } from '../../components/ChallengeTitleBlock/ChallengeTitleBlock';
import { ScoringRuleRow } from './components/ScoringRuleRow/ScoringRuleRow';
import { TitleRowDesktop } from './components/TitleRow/TitleRowDesktop/TitleRowDesktop';
import { leaderboardStore } from '../../stores/leaderboardStore';
import { getRouteParam, goTo } from '../../../shared/utils/angularUtils';
import { FiltersRow } from './components/FiltersRow/FiltersRow';
import { TitleRowMobile } from './components/TitleRow/TitleRowMobile/TitleRowMobile';
import { LeaderboardRowMobile } from './components/LeaderboardRow/LeaderboardRowMobile/LeaderboardRowMobile';
import { LeaderboardRowDesktop } from './components/LeaderboardRow/LeaderboardRowDesktop/LeaderboardRowDesktop';
import { LbHorizontalScrollContainer } from '../../../shared/components/LbHorizontalScrollContainer/LbHorizontalScrollContainer';
import { columnsConfig } from '../../constants/leaderboardColumn';
import { CHALLENGE_STATUS } from '../../constants/challengeStatus';
import { getIsRegistrationPeriod } from '../../utils/getIsRegistrationPeriod/getIsRegistrationPeriod';
import { SCORING_RULE } from '../../constants/scoringRule';

const NgzChallengeLeaderboard = observer(() => {
  const { width } = useWindowSize();
  const isMobile = width < 768;

  const {
    challengeInfo,
    leaderboardList,
    getChallengeInfo,
    getLeaderboardList,
    setDefaultValues,
    filterMethods,
  } = leaderboardStore;

  const {
    isShowPlaceholder,
    isSearchUsed,
    getPaginationProps,
    elementsPerPage,
    totalElements,
  } = filterMethods;

  const challengeId = getRouteParam('challengeId');

  const getData = async () => {
    await getChallengeInfo(challengeId);
    await getLeaderboardList();
  };

  useEffect(() => {
    getData();

    return () => {
      setDefaultValues();
    };
  }, [challengeId]);

  if (!challengeInfo) {
    return null;
  }

  const leaderboardColumns =
    columnsConfig[challengeInfo.scoringRules || SCORING_RULE.FAT_LOSS];

  const handleOpenChallengeClick = () => {
    if (challengeInfo.status === CHALLENGE_STATUS.DRAFT) {
      goTo(`/challenge-preview/${challengeId}`);
    } else {
      goTo(`/challenge-landing/${challengeId}`);
    }
  };

  // -2 because we have custom columns layout for rank and customer columns
  const dynamicColumnsLength = leaderboardColumns.length - 2;

  const isRegistrationPeriod =
    !!challengeInfo.importantDates &&
    getIsRegistrationPeriod(
      challengeInfo.importantDates.registrationStartDate,
      challengeInfo.importantDates.registrationEndDate,
    );

  const hasChallengeStarted =
    challengeInfo.status === CHALLENGE_STATUS.ACTIVE ||
    challengeInfo.status === CHALLENGE_STATUS.FINISHED;

  const emptyPlaceholderDescription =
    isRegistrationPeriod || hasChallengeStarted
      ? 'There are no participants'
      : 'The challenge hasn’t started';

  return (
    <>
      <ChallengesHeader />
      <Container maxWidth="lg" className={classes.container}>
        <BlackBgRow bgRowHeight={isMobile ? '506px' : '420px'}>
          <ChallengeTitleBlock
            challengeId={challengeId}
            openButtonTitle="Open Challenge"
            title={
              <>
                {challengeInfo.challengeName}{' '}
                <span className={classes.leaderboardTitle}>LEADERBOARD</span>
              </>
            }
            isRegistrationPeriod={isRegistrationPeriod}
            challengeStatus={challengeInfo.status}
            description={challengeInfo.description}
            onOpenButtonClick={handleOpenChallengeClick}
          />
          <FiltersRow />
          <ScoringRuleRow />
          <LbHorizontalScrollContainer
            scrollBarClassName={classes.leaderboardTableScrollBar}
          >
            <div
              className={classes.leaderboardTable}
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile
                  ? 'auto 1fr auto'
                  : `auto 1.25fr repeat(${dynamicColumnsLength}, minmax(120px, 1fr))`,
              }}
            >
              {isMobile ? (
                <TitleRowMobile />
              ) : (
                <TitleRowDesktop leaderboardColumns={leaderboardColumns} />
              )}
              {leaderboardList.map((item) =>
                isMobile ? (
                  <LeaderboardRowMobile
                    key={item.participantId}
                    leaderboardRow={item}
                    leaderboardColumns={leaderboardColumns}
                  />
                ) : (
                  <LeaderboardRowDesktop
                    key={item.participantId}
                    leaderboardRow={item}
                    leaderboardColumns={leaderboardColumns}
                  />
                ),
              )}
            </div>
          </LbHorizontalScrollContainer>
          {isShowPlaceholder && (
            <Paper variant="roundedBox">
              <EmptyList
                show
                isSearch={isSearchUsed}
                description={emptyPlaceholderDescription}
              />
            </Paper>
          )}
          <LbPagination
            variant="outBox"
            {...getPaginationProps()}
            elementsPerPage={elementsPerPage}
            totalElements={totalElements}
          />
        </BlackBgRow>
      </Container>
    </>
  );
});

angularizeWithProviders(NgzChallengeLeaderboard, 'ngzChallengeLeaderboard', {});
